import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCollegeContext } from '../../contexts/CollegeContext';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { supabase } from '../../lib/supabase';
import { useFrameworkAutosave, SaveStatusIndicator } from '../../hooks/useFrameworkAutosave.jsx';
import { 
  ArrowLeft, 
  Copy, 
  CheckCircle, 
  Palette,
  Type,
  Save,
  RefreshCw,
  FileText,
  Settings,
  Eye,
  Sparkles,
  X
} from 'lucide-react';

const FormattingCustomizer = () => {
  const navigate = useNavigate();
  const { currentUser, familyUsers, family } = useFamilyContext();
  const { updateMasterStrategy } = useCollegeContext();
  
  const [loading, setLoading] = useState(true);
  const [studentProfile, setStudentProfile] = useState(null);
  const [sectionTitles, setSectionTitles] = useState({
    strategicAdvantage: "Your Strategic Advantage",
    intellectualSignature: "How Your Brain Actually Works",
    hiddenSuperpowers: "Your Secret Weapons",
    strategicPositioning: "How You Stand Out",
    essayStrategy: "Essay Game Plan",
    collegeRecommendations: "College Recommendations",
    actionPlan: "Do This First (Before You Forget)",
    successMetrics: "Success Metrics - Know You're Winning"
  });
  
  const [tonePreferences, setTonePreferences] = useState({
    competitive: false,
    collaborative: true,
    growth: false
  });
  
  const [metaphorStyle, setMetaphorStyle] = useState('supportive');
  const [formattingInstructions, setFormattingInstructions] = useState('');
  const [copySuccess, setCopySuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedStudentUserId, setSelectedStudentUserId] = useState(null);
  
  // Initialize autosave hook
  const { 
    saveStatus, 
    lastSaved, 
    errorMessage, 
    save: autoSave, 
    saveNow,
    clearError 
  } = useFrameworkAutosave(selectedStudentUserId, 2000);
  
  // Personality profile state
  const [personalityProfile, setPersonalityProfile] = useState({
    motivationStyle: '',
    communicationPreference: '',
    energySource: '',
    decisionMaking: '',
    feedbackStyle: '',
    identityFramework: ''
  });
  
  // Extended formatting preferences for comprehensive instruction generation
  const [languageTransformations, setLanguageTransformations] = useState({
    competitiveToCollaborative: {
      'competitive advantage': 'unique contribution',
      'stand out': 'enhance communities',
      'beat other applicants': 'find programs that value your perspective',
      'strategic positioning': 'authentic representation',
      'win admissions': 'find genuine fit',
      'dominate': 'excel in',
      'weapons': 'strengths',
      'tactical advantage': 'strategic asset'
    },
    pressureToSupport: {
      'must do': 'opportunity to',
      'required': 'beneficial',
      'deadline pressure': 'thoughtful timeline',
      'maximize': 'authentically develop',
      'optimize': 'genuinely express',
      'prove superiority': 'demonstrate capability',
      'outperform': 'contribute meaningfully'
    }
  });
  
  // Dynamic section conceptual frames based on tone preferences
  const getSectionConceptualFrames = useCallback(() => {
    if (tonePreferences.competitive && !tonePreferences.collaborative && !tonePreferences.growth) {
      // Sheppard type - competitive/strategic
      return {
        strategicAdvantage: {
          newTitle: 'Your Strategic Advantage',
          conceptualFrame: 'What makes you uniquely positioned to excel',
          focus: 'How your background gives you competitive advantages'
        },
        intellectualSignature: {
          newTitle: 'How Your Brain Actually Works', 
          conceptualFrame: 'Your distinctive analytical and problem-solving capabilities',
          focus: 'Demonstrate your intellectual superiority and strategic thinking'
        },
        hiddenSuperpowers: {
          newTitle: 'Your Secret Weapons',
          conceptualFrame: 'Unique capabilities that give you advantages over other applicants', 
          focus: 'Help you recognize and leverage your competitive strengths'
        },
        strategicPositioning: {
          newTitle: 'How You Stand Out',
          conceptualFrame: 'Strategic positioning for maximum competitive advantage',
          focus: 'How to position yourself to win admissions'
        },
        essayStrategy: {
          newTitle: 'Essay Game Plan',
          conceptualFrame: 'Strategic messaging to maximize admissions success',
          focus: 'Stories that demonstrate your competitive advantages'
        },
        collegeRecommendations: {
          newTitle: 'College Recommendations',
          conceptualFrame: 'Strategic targeting of programs where you have advantages',
          focus: 'Schools where you can excel and stand out'
        },
        actionPlan: {
          newTitle: 'Do This First (Before You Forget)',
          conceptualFrame: 'Immediate actions to take before you forget',
          focus: 'Actions that create competitive momentum'
        },
        successMetrics: {
          newTitle: 'Success Metrics - Know You\'re Winning',
          conceptualFrame: 'Checkpoints to validate your competitive advantage',
          focus: 'Metrics that show you\'re winning'
        },
      };
    } else if (tonePreferences.collaborative && !tonePreferences.competitive) {
      // Lila type - collaborative/affirmative
      return {
        strategicAdvantage: {
          newTitle: 'Your Unique Contribution',
          conceptualFrame: 'What you bring TO programs rather than what you need to prove',
          focus: 'How your background enhances program communities'
        },
        intellectualSignature: {
          newTitle: 'How Your Mind Actually Works', 
          conceptualFrame: 'Recognition of sophisticated thinking patterns',
          focus: 'Validate your distinctive thinking approach'
        },
        hiddenSuperpowers: {
          newTitle: 'Your Distinctive Strengths',
          conceptualFrame: 'Reframe undervalued traits as valuable capabilities', 
          focus: 'Help you see unique qualities as sophisticated skills'
        },
        strategicPositioning: {
          newTitle: 'How You Stand Out',
          conceptualFrame: 'Community contribution rather than strategic positioning',
          focus: 'How you create positive change and add value'
        },
        essayStrategy: {
          newTitle: 'Your Story Strategy',
          conceptualFrame: 'Sharing meaningful experiences rather than strategic messaging',
          focus: 'Stories that show your capacity to create positive impact'
        },
        collegeRecommendations: {
          newTitle: 'Schools You\'ll Love (that will want you too)',
          conceptualFrame: 'Finding communities where you belong rather than strategic targeting',
          focus: 'Authentic fit and mutual benefit'
        },
        actionPlan: {
          newTitle: 'Do This First (Before You Forget)',
          conceptualFrame: 'Immediate actions to take before you forget',
          focus: 'Actions that create competitive momentum'
        },
        successMetrics: {
          newTitle: 'Success Metrics - Know You\'re Winning',
          conceptualFrame: 'Checkpoints to validate your competitive advantage',
          focus: 'Metrics that show you\'re winning'
        },
      };
    }
    
    // Default case - Alex type - growth/balanced (ensure we always return something)
    return {
      strategicAdvantage: {
        newTitle: 'Your Strategic Edge',
        conceptualFrame: 'What your growth journey demonstrates about your potential',
        focus: 'How your adaptability and learning make you valuable'
      },
      intellectualSignature: {
        newTitle: 'How Your Mind Actually Works', 
        conceptualFrame: 'Your approach to learning and growth through challenge',
        focus: 'Validate your growth mindset and adaptive thinking'
      },
      hiddenSuperpowers: {
        newTitle: 'Your Most Powerful Strengths',
        conceptualFrame: 'Skills developed through overcoming challenges', 
        focus: 'Help you see your resilience as a sophisticated capability'
      },
      strategicPositioning: {
        newTitle: 'How You Stand Out',
        conceptualFrame: 'Balancing personal growth with community contribution',
        focus: 'How you create value through continuous development'
      },
      essayStrategy: {
        newTitle: 'Your Essay Strategy',
        conceptualFrame: 'Showcasing growth and potential through authentic stories',
        focus: 'Stories that demonstrate your capacity for development and impact'
      },
      collegeRecommendations: {
        newTitle: 'Programs for Growth',
        conceptualFrame: 'Finding environments that support continued development',
        focus: 'Schools that value potential and growth mindset'
      },
      actionPlan: {
        newTitle: 'Do This First (Before You Forget)',
        conceptualFrame: 'Immediate actions to take before you forget',
        focus: 'Actions that create competitive momentum'
      },
      successMetrics: {
        newTitle: 'Success Metrics - Know You\'re Winning',
        conceptualFrame: 'Checkpoints to validate your competitive advantage',
        focus: 'Metrics that show you\'re winning'
      },
    };
  }, [tonePreferences]);

  // Dynamic content adaptation based on tone preferences
  const getContentAdaptationStyle = useCallback(() => {
    if (tonePreferences.competitive && !tonePreferences.collaborative && !tonePreferences.growth) {
      // Sheppard type - competitive/strategic
      return {
        focusType: 'achievement',
        orientation: 'individual', 
        approachStyle: 'competitive',
        developmentEmphasis: 'strategic',
        belongingLanguage: false
      };
    } else if (tonePreferences.collaborative && !tonePreferences.competitive) {
      // Lila type - collaborative/affirmative
      return {
        focusType: 'affirmation',
        orientation: 'community',
        approachStyle: 'collaborative', 
        developmentEmphasis: 'authentic',
        belongingLanguage: true
      };
    } else {
      // Alex type - growth/balanced
      return {
        focusType: 'contribution',
        orientation: 'hybrid',
        approachStyle: 'balanced',
        developmentEmphasis: 'practical', 
        belongingLanguage: true
      };
    }
  }, [tonePreferences]);

  // LoadPhase6Data function definition
  const loadPhase6Data = useCallback(async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      
      const savedStudentId = localStorage.getItem('college-selected-student-id');
      
      if (!savedStudentId) {
        console.error('No valid student user ID found. Please ensure a student is selected.');
        setLoading(false);
        return;
      }
      
      // savedStudentId should already be the student's user_id that we need
      // The parent selected this student, and we're customizing for them
      const userIdForQuery = savedStudentId;
      console.log('🔍 FormattingCustomizer: Customizing for student:', userIdForQuery);
      console.log('📝 Note: Parent is customizing framework for their student');
      
      setSelectedStudentUserId(userIdForQuery);
      
      console.log('🔍 FormattingCustomizer: Loading Phase 6 data for student user_id:', userIdForQuery);
      
      const { data: profileData, error } = await supabase
        .from('student_academic_profiles')
        .select(`
          name,
          section_titles,
          tone_preferences,
          metaphor_style,
          personality_profile,
          formatting_instructions,
          synthesis_instructions,
          assessment_phase,
          language_transformations,
          section_conceptual_frames,
          content_adaptation_style,
          visual_elements,
          career_goals,
          strategic_context_summary,
          intended_major,
          academic_interests
        `)
        .eq('user_id', userIdForQuery)
        .single();

      if (error) {
        console.error('❌ FormattingCustomizer: Database error:', error);
      }

      if (profileData) {
        setStudentProfile({
          name: profileData.name || 'Student',
          career_goals: profileData.career_goals,
          strategic_context_summary: profileData.strategic_context_summary,
          academic_focus: profileData.intended_major || (profileData.academic_interests && profileData.academic_interests.length > 0 ? profileData.academic_interests.join(', ') : null)
        });

        // Load saved formatting preferences if available
        if (profileData.section_titles) {
          setSectionTitles(profileData.section_titles);
        }
        
        if (profileData.tone_preferences) {
          setTonePreferences(profileData.tone_preferences);
        }
        
        if (profileData.metaphor_style) {
          setMetaphorStyle(profileData.metaphor_style);
        }
        
        if (profileData.formatting_instructions) {
          setFormattingInstructions(profileData.formatting_instructions);
        }
        
        if (profileData.personality_profile) {
          setPersonalityProfile(profileData.personality_profile);
        }
        
        // Load expanded formatting preferences
        if (profileData.language_transformations) {
          setLanguageTransformations(profileData.language_transformations);
        }
        
        if (profileData.section_conceptual_frames) {
          setSectionConceptualFrames(profileData.section_conceptual_frames);
        }
        
        if (profileData.content_adaptation_style) {
          setContentAdaptationStyle(profileData.content_adaptation_style);
        }
        
        if (profileData.visual_elements) {
          setVisualElements(profileData.visual_elements);
        }
        
        console.log('✅ FormattingCustomizer: Loaded Phase 6 data:', profileData);
      }
    } catch (error) {
      console.error('Error loading Phase 6 data:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // State declarations after function definitions to avoid temporal dead zone
  const [sectionConceptualFrames, setSectionConceptualFrames] = useState(() => getSectionConceptualFrames());
  const [contentAdaptationStyle, setContentAdaptationStyle] = useState(() => getContentAdaptationStyle());
  
  const [visualElements, setVisualElements] = useState({
    useIcons: true,
    iconStyle: 'meaningful', // meaningful, decorative, minimal
    emphasisStyle: 'bold', // bold, italic, underline
    blockquoteStyle: 'key-takeaways', // key-takeaways, quotes, highlights
    structurePreference: 'detailed' // detailed, simple, minimal
  });

  useEffect(() => {
    loadPhase6Data();
  }, [currentUser, familyUsers, family, loadPhase6Data]);

  // Update content adaptation style when tone preferences change
  useEffect(() => {
    setContentAdaptationStyle(getContentAdaptationStyle());
    setSectionConceptualFrames(getSectionConceptualFrames());
  }, [tonePreferences, getContentAdaptationStyle, getSectionConceptualFrames]);
  
  // Autosave when customization data changes
  useEffect(() => {
    if (!selectedStudentUserId || loading) return;
    
    // Only autosave if we have some customization content
    const hasContent = formattingInstructions.trim() || 
                       Object.keys(sectionTitles).length > 0 ||
                       Object.keys(personalityProfile).some(key => personalityProfile[key]);
    
    if (hasContent) {
      autoSave({
        section_titles: sectionTitles,
        tone_preferences: tonePreferences,
        metaphor_style: metaphorStyle,
        formatting_instructions: formattingInstructions.trim(),
        language_transformations: languageTransformations,
        section_conceptual_frames: sectionConceptualFrames,
        content_adaptation_style: contentAdaptationStyle,
        visual_elements: visualElements,
        personality_profile: personalityProfile,
        assessment_phase: 6
      });
    }
  }, [
    sectionTitles, 
    tonePreferences, 
    metaphorStyle, 
    formattingInstructions, 
    languageTransformations,
    sectionConceptualFrames,
    contentAdaptationStyle,
    visualElements,
    personalityProfile,
    selectedStudentUserId, 
    loading, 
    autoSave
  ]);

  const metaphorStyles = {
    strategic: {
      name: "Military/Strategic",
      description: "Uses terms like weapons, tactics, positioning, advantage",
      examples: ["Secret Weapons", "Strategic Advantage", "Stand Out", "Deploy"]
    },
    supportive: {
      name: "Supportive/Growth",
      description: "Uses terms like strengths, abilities, opportunities, develop",
      examples: ["Unique Strengths", "Your Edge", "Authentic Path", "Build"]
    },
    athletic: {
      name: "Athletic/Performance",
      description: "Uses terms like training, game plan, winning, champion",
      examples: ["Game Plan", "Winning Edge", "Training Ground", "Score"]
    },
    journey: {
      name: "Journey/Adventure",
      description: "Uses terms like path, exploration, discovery, navigate",
      examples: ["Your Journey", "Explore", "Discover", "Navigate"]
    }
  };

  const generateFormattingPrompt = () => {
    const studentName = studentProfile?.name || '[student name]';
    
    // Determine student type for customization
    const studentType = (() => {
      if (tonePreferences.competitive && !tonePreferences.collaborative && !tonePreferences.growth) {
        return 'Competitive/Strategic';
      } else if (tonePreferences.collaborative && !tonePreferences.competitive) {
        return 'Collaborative/Affirmative';
      }
      return 'Balanced/Growth';
    })();

  return `**PRIMARY TASK:** Create transformation instructions to guide our AI in converting raw assessment insights into a specifically structured JSON object that will be used to output a formatted report.
**STUDENT CONTEXT:**
- Name: ${studentName}
- Academic Focus: ${studentProfile?.intended_major || studentProfile?.academic_interests || 'N/A'}
- Career Goals: ${studentProfile?.career_goals || 'N/A'}
- Metaphor Style: ${metaphorStyles[metaphorStyle]?.name}

**THE INSTRUCTIONS MUST ENSURE THE API RESPONSE MATCHES THIS EXACT FORMAT:**

# JSON Transformation Instructions for ${studentName}'s Insights Report

## STUDENT-SPECIFIC PROFILE:
\`\`\`json
{
  "name": "${studentName}",
  "academicFocus": "${studentProfile?.intended_major || studentProfile?.academic_interests || 'N/A'}",
  "careerGoal": "${studentProfile?.career_goals || 'N/A'}"
  }
\`\`\`

## ${studentName.toUpperCase()}-SPECIFIC TONE TRANSFORMATION:

### Preferred Language Patterns:
${tonePreferences.competitive ? '✓ **Strategic/Competitive Terms:** competitive advantage, outperform, strategic edge, win, dominate, tactical superiority' : '✗ **Avoid Competitive Language**'}
${tonePreferences.collaborative ? '✓ **Collaborative/Community Terms:** contribute, community, together, support, enhance, belonging, connection' : '✗ **Avoid Overly Collaborative Language**'}
${tonePreferences.growth ? '✓ **Growth/Development Terms:** develop, journey, potential, evolve, adapt, transform, progress' : '✗ **Avoid Growth-Focused Language**'}

### Language Transformations:
${!tonePreferences.competitive ? Object.entries(languageTransformations.competitiveToCollaborative).map(([from, to]) => `- "${from}" → "${to}"`).join('\n') : Object.entries(languageTransformations.competitiveToCollaborative).map(([from, to]) => `- "${to}" → "${from}"`).join('\n')}

## ${studentName.toUpperCase()}-SPECIFIC METAPHOR INTEGRATION:

**${metaphorStyles[metaphorStyle]?.name} Metaphors:**
Uses ${metaphorStyles[metaphorStyle]?.description}
Examples: ${metaphorStyles[metaphorStyle]?.examples.join(', ')}

## JSON STRUCTURE REQUIREMENTS:

\`\`\`json
{
  "studentProfile": {
    "name": "${studentName}",
    "academicFocus": "[from assessment]",
    "careerGoal": "[from assessment]",
    "personalityFramework": "${personalityProfile.identityFramework}",
    "positioningNarrative": "[from assessment analysis]"
  },
  "insights": {
    "strategicAdvantage": {
      "title": "${sectionTitles.strategicAdvantage}",
      "competitiveHeadline": "[One powerful sentence for ${studentName}]",
      "primaryAdvantage": "[THE ONE thing that makes ${studentName} unique]",
      "tacticalApplications": {
        "essayStrategy": "[Specific to ${studentName}'s experiences]",
        "collegeResearch": "[Based on ${studentName}'s goals]",
        "interviewPrep": "[Aligned with ${studentName}'s strengths]"
      },
      "competitiveIntelligence": "[What others miss that ${studentName} should emphasize]"
    },
    "intellectualSignature": {
      "title": "${sectionTitles.intellectualSignature}",
      "icon": "🧠",
      "content": "[350 words describing ${studentName}'s unique thinking style with specific examples]",
      "brandStatement": "[One-sentence intellectual brand for ${studentName}]",
      "leveragePoints": "[Concrete ways ${studentName} can showcase this in applications]"
    },
    "hiddenSuperpowers": {
      "title": "${sectionTitles.hiddenSuperpowers}",
      "icon": "⚡",
      "content": "[400 words identifying 4 specific advantages ${studentName} has]",
      "weapons": [
        {
          "trait": "[Specific strength from ${studentName}'s responses]",
          "advantage": "[How this creates competitive advantage]",
          "deployment": "[Specific application strategy]"
        }
      ]
    },
    "strategicPositioning": {
      "title": "${sectionTitles.strategicPositioning}",
      "icon": "🏆",
      "content": "[300 words on ${studentName}'s unique market position]",
      "brandStatement": "[Personal brand statement for ${studentName}]",
      "positioning": "[Why ${studentName}'s authentic approach beats generic positioning]",
      "targetPrograms": "[3 specific programs that especially value ${studentName}'s profile]"
    },
    "essayStrategy": {
      "title": "${sectionTitles.essayStrategy}",
      "icon": "📝",
      "content": "[500 words of comprehensive application tactics for ${studentName}]",
      "mainTheme": "[Central theme for ${studentName}'s applications]",
      "bestStory": "[Most compelling story from ${studentName}'s experiences]",
      "essayTopics": "[4 specific essay topics tailored to ${studentName}]",
      "avoidTraps": "[Common mistakes for ${studentName}'s profile type]"
    },
    "collegeRecommendations": {
      "title": "${sectionTitles.collegeRecommendations}",
      "icon": "🎓",
      "reachSchools": "[3 reach schools with strategic fit analysis for ${studentName}]",
      "targetSchools": "[3 target schools with probability assessment for ${studentName}]",
      "safetySchools": "[3 safety schools with merit aid potential for ${studentName}]",
      "strategicFit": "[How each recommendation aligns with ${studentName}'s goals]"
    },
    "actionPlan": {
      "title": "${sectionTitles.actionPlan}",
      "icon": "🚀",
      "content": "[300 words of prioritized actions for ${studentName}]",
      "urgentActions": "[🔴 This week's critical tasks for ${studentName}]",
      "monthlyGoals": "[🟡 30-day strategic tasks for ${studentName}]",
      "quarterlyStrategy": "[🟢 Long-term positioning moves for ${studentName}]",
      "timeline": "[Specific deadlines relevant to ${studentName}'s application strategy]"
    },
    "successMetrics": {
      "title": "${sectionTitles.successMetrics}",
      "icon": "📊",
      "content": "[200 words of validation checkpoints for ${studentName}]",
      "differentiationTest": "[Can ${studentName} explain their unique value in 30 seconds?]",
      "competitiveCheck": "[Is ${studentName} positioned differently from similar applicants?]",
      "executionTracker": "[Is ${studentName} taking concrete weekly actions?]",
      "alignmentReview": "[Do ${studentName}'s targets match their advantages?]"
    }
  }
}
\`\`\`

## ${studentName.toUpperCase()}-SPECIFIC CONTENT ADAPTATION RULES:

### Content Adaptation Based on ${studentType} Profile:
1. **Every strength** should ${contentAdaptationStyle.focusType === 'achievement' ? 'connect to measurable competitive advantages' : contentAdaptationStyle.focusType === 'affirmation' ? 'affirm their value and distinctive qualities' : 'emphasize their contribution to communities'}
2. **Every school recommendation** should emphasize ${contentAdaptationStyle.belongingLanguage ? 'values alignment and belonging' : 'strategic fit and probability of success'}
3. **Every action item** should ${contentAdaptationStyle.focusType === 'achievement' ? 'include concrete deadlines and measurable outcomes' : 'respect their collaborative, authentic approach'}
4. **All framing** should be ${contentAdaptationStyle.orientation === 'individual' ? 'achievement-focused rather than relationship-focused' : 'contribution-focused rather than achievement-focused'}
5. **Tone throughout** should be ${contentAdaptationStyle.focusType === 'achievement' ? 'data-driven and strategically confident' : contentAdaptationStyle.focusType === 'affirmation' ? 'affirming and recognition-based' : 'contribution-focused and community-oriented'}

### Teen-Specific Language Requirements:
- **Lead with Evidence:** "When you [specific example from responses], that showed..."
- **Reality Checks:** "Real talk: Here's what most applicants miss..."
- **Insider Knowledge:** "Here's the thing nobody tells you about..."
- **Immediate Value:** "Do this: [specific actionable step]"

### Skepticism Busters (Critical for Teen Engagement):
- **Acknowledge Intelligence:** "You probably already know [obvious thing]. Here's what's not obvious..."
- **Prove Immediate Value:** "Use this opening line in your essay:"
- **Show Real Stakes:** "The difference between doing this and not is [specific outcome]"
- **Competitive Context:** "While others do [typical approach], you should [unique approach]"

### Strategic Advantage Structure Requirements:
The strategicAdvantage section MUST include:
- **Competitive Headline:** One powerful sentence with immediate impact
- **Primary Advantage:** Evidence-based unique differentiator with institutional value
- **Tactical Applications:** Immediate 24-48 hour actions for essays, research, interviews
- **Competitive Intelligence:** Strategic positioning vs. similar applicants

### College Recommendations Requirements (CRITICAL):
- **Every school recommendation** MUST emphasize both strategic fit AND probability of success
- Include competitive positioning analysis for each school level
- Address merit aid potential and strategic value beyond admission odds
- Provide specific tactical application approaches based on probability assessment

### Language Enhancements for JSON:
- **Evidence-First:** Every claim backed by specific examples from their responses
- **Immediately Actionable:** Every insight includes tactical deployment within 24-48 hours
- **Competitively Aware:** Position against actual similar applicants with probability analysis
- **Authentically Empowering:** Build confidence in genuine advantages with realistic success probability

### Tone Consistency Requirements:
- **Confident Strategic Consultant:** Elite admissions expertise with teen authenticity
- **Specific and Tactical:** Every recommendation includes HOW to implement
- **Personalized Depth:** Use their actual experiences and demonstrated patterns
- **Urgently Strategic:** Balance immediate actions with long-term positioning

### Visual Enhancement Requirements:
- **🎯 STRATEGIC ADVANTAGE:** Competitive insights that create immediate advantage
- **💡 THIS WEEK'S MOVE:** Urgent tactical steps with clear reasoning
- **🔍 INSIDER INTEL:** What others don't know but should understand
- **⚠️ AVOID THIS TRAP:** Common mistakes for their specific profile type
- **✅ IMMEDIATE ACTION:** Specific steps they can take within 24 hours

## TRANSFORMATION QUALITY CONTROLS:

### Pre-JSON Validation for ${studentName}:
- Does every insight emphasize ${studentName}'s specific strengths from their assessment?
- Are recommendations specific to their academic focus and career goals?
- Does language reflect their ${studentType} preferences?
- Are all examples drawn from their actual responses?

### Success Criteria:
The formatted report should make ${studentName} think:
- "Finally, someone actually gets what makes me different"
- "I have specific advantages I didn't realize I had" 
- "I know exactly what to do next and why it matters"
- "This is the best investment of time I've made in my college process"

IMPORTANT: Make every section reference ${studentName} by name and include specific details from their assessment responses. This should read like instructions written specifically for ${studentName}, not generic instructions.`;
};


  const copyPromptToClipboard = async () => {
    const prompt = generateFormattingPrompt();
    try {
      await navigator.clipboard.writeText(prompt);
      setCopySuccess('copied');
      setTimeout(() => setCopySuccess(''), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      setCopySuccess('failed');
      setTimeout(() => setCopySuccess(''), 2000);
    }
  };

  const handleToneToggle = (tone) => {
    setTonePreferences(prev => ({
      ...prev,
      [tone]: !prev[tone]
    }));
  };

  const saveFormattingPreferences = async () => {
    if (!selectedStudentUserId) {
      console.error('❌ No student user ID available');
      alert('Please select a student before saving');
      return false;
    }

    setSaving(true);
    try {
      console.log('🔍 DEBUG: Saving formatting preferences for user:', selectedStudentUserId);
      
      // Use the hook's saveNow method for immediate save
      const success = await saveNow({
        section_titles: sectionTitles,
        tone_preferences: tonePreferences,
        metaphor_style: metaphorStyle,
        formatting_instructions: formattingInstructions.trim(),
        language_transformations: languageTransformations,
        section_conceptual_frames: sectionConceptualFrames,
        content_adaptation_style: contentAdaptationStyle,
        visual_elements: visualElements,
        personality_profile: personalityProfile,
        assessment_phase: 6
      });

      if (success) {
        console.log('✅ SUCCESS: Formatting preferences saved to database');
        
        // Update master strategy
        await updateMasterStrategy({
          section_titles: sectionTitles,
          tone_preferences: tonePreferences,
          metaphor_style: metaphorStyle,
          personality_profile: personalityProfile,
          formatting_instructions: formattingInstructions.trim(),
          phase_6_completed: true,
          updated_at: new Date().toISOString()
        });

        console.log('✅ Formatting preferences saved successfully');
        
        // Show success message
        alert('Formatting preferences saved successfully!');
        
        // Navigate back to hub or assessment
        navigate('/college/parent-testing');
        return true; // Return success
      } else {
        throw new Error('Save operation failed');
      }
    } catch (error) {
      console.error('Error saving formatting preferences:', error);
      alert('Failed to save formatting preferences. Please try again. Error: ' + error.message);
      return false; // Return failure
    } finally {
      setSaving(false);
    }
  };

  const PreviewModal = () => {
    if (!showPreview) return null;

    return (
      <div className="fixed inset-0 bg-slate-900 bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold text-slate-900">Preview Format Changes</h3>
            <button
              onClick={() => setShowPreview(false)}
              className="text-slate-400 hover:text-slate-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="space-y-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <h4 className="font-medium text-slate-900 mb-2">Section Titles</h4>
              <div className="space-y-1 text-sm">
                {Object.entries(sectionTitles).map(([key, title]) => (
                  <div key={key} className="flex justify-between">
                    <span className="text-slate-600">{key}:</span>
                    <span className="font-medium">{title}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-slate-50 rounded-lg p-4">
              <h4 className="font-medium text-slate-900 mb-2">Language Style</h4>
              <p className="text-sm text-slate-600">
                {metaphorStyles[metaphorStyle].description}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {metaphorStyles[metaphorStyle].examples.map((example, idx) => (
                  <span key={idx} className="px-2 py-1 bg-white rounded text-xs">
                    {example}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Loading formatting options</h2>
          <p className="text-slate-600">Preparing customization interface...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigate('/college/synthesis-instructions')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Synthesis Instructions
            </button>
            <button
              onClick={() => setShowPreview(true)}
              className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900"
            >
              <Eye className="w-4 h-4" />
              Preview Changes
            </button>
          </div>
          
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 rounded-full border border-slate-700 mb-4">
              <Palette className="w-5 h-5 text-white" />
              <span className="text-sm font-medium text-white">Phase 6: Format Customization</span>
            </div>
            <h1 className="text-4xl font-bold text-slate-900 mb-4">
              Customize Output Format
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Tailor the language, tone, and structure of the final insights to resonate 
              perfectly with {studentProfile?.name || 'your student'}.
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto space-y-8">
          {/* Section Titles */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Type className="w-5 h-5" />
              Section Titles
            </h2>
            <p className="text-slate-600 mb-6">
              Customize how each section is titled to match your preferred language style.
            </p>
            
            <div className="grid gap-4">
              {Object.entries(sectionTitles).map(([key, value]) => (
                <div key={key} className="flex items-center gap-4">
                  <label className="text-sm font-medium text-slate-700 w-48">
                    {key.replace(/([A-Z])/g, ' $1').trim()}:
                  </label>
                  <input
                    type="text"
                    value={value}
                    onChange={(e) => setSectionTitles(prev => ({
                      ...prev,
                      [key]: e.target.value
                    }))}
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Style Preview */}
          <div className="bg-slate-50 rounded-2xl p-6 mb-8 border border-slate-200">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Current Style Preview</h3>
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-slate-700">Output Style: </span>
                <span className="text-sm text-slate-900">
                  {tonePreferences.competitive && !tonePreferences.collaborative && !tonePreferences.growth ? 
                    'Strategic/Competitive (like Sheppard) - Achievement-focused with data-driven language' : 
                    tonePreferences.collaborative && !tonePreferences.competitive ?
                    'Collaborative/Supportive (like Lila) - Community-focused with affirming language' :
                    'Growth/Balanced (like Alex) - Journey-focused with development language'}
                </span>
              </div>
              <div>
                <span className="text-sm font-medium text-slate-700">Example Strength: </span>
                <span className="text-sm text-slate-900">
                  {tonePreferences.competitive && !tonePreferences.collaborative && !tonePreferences.growth ? 
                    '"Analytical Superiority: Outperforms 95% of applicants in quantitative analysis"' : 
                    tonePreferences.collaborative && !tonePreferences.competitive ?
                    '"Bridge-Building: Creates inclusive environments where everyone thrives"' :
                    '"Adaptive Learning: Continuously evolves skills through challenges and reflection"'}
                </span>
              </div>
              <div>
                <span className="text-sm font-medium text-slate-700">Icons: </span>
                <span className="text-sm text-slate-900">
                  {tonePreferences.competitive && !tonePreferences.collaborative && !tonePreferences.growth ? 
                    '⚔️ 🎯 📊 🏆 (Strategic/Military)' : 
                    tonePreferences.collaborative && !tonePreferences.competitive ?
                    '✨ 🧠 💫 🌟 (Supportive/Growth)' :
                    '🌱 🎯 📈 🌟 (Growth/Journey)'}
                </span>
              </div>
            </div>
          </div>

          {/* Tone Preferences */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Tone Preferences
            </h2>
            <p className="text-slate-600 mb-6">
              Select which language styles should be used in the final report.
            </p>
            
            <div className="space-y-4">
              <label className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100">
                <input
                  type="checkbox"
                  checked={tonePreferences.competitive}
                  onChange={() => handleToneToggle('competitive')}
                  className="w-5 h-5 text-slate-900 rounded"
                />
                <div>
                  <div className="font-medium text-slate-900">Competitive Language</div>
                  <div className="text-sm text-slate-600">Uses terms like "stand out", "advantage", "weapons", "win"</div>
                </div>
              </label>
              
              <label className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100">
                <input
                  type="checkbox"
                  checked={tonePreferences.collaborative}
                  onChange={() => handleToneToggle('collaborative')}
                  className="w-5 h-5 text-slate-900 rounded"
                />
                <div>
                  <div className="font-medium text-slate-900">Collaborative Language</div>
                  <div className="text-sm text-slate-600">Uses terms like "contribute", "community", "together", "support"</div>
                </div>
              </label>
              
              <label className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100">
                <input
                  type="checkbox"
                  checked={tonePreferences.growth}
                  onChange={() => handleToneToggle('growth')}
                  className="w-5 h-5 text-slate-900 rounded"
                />
                <div>
                  <div className="font-medium text-slate-900">Growth Language</div>
                  <div className="text-sm text-slate-600">Uses terms like "develop", "journey", "potential", "evolve"</div>
                </div>
              </label>
            </div>
          </div>

          {/* Metaphor Style */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              Metaphor Style
            </h2>
            <p className="text-slate-600 mb-6">
              Choose the overall metaphorical framework for the insights.
            </p>
            
            <div className="grid gap-4">
              {Object.entries(metaphorStyles).map(([key, style]) => (
                <label key={key} className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100">
                  <input
                    type="radio"
                    name="metaphorStyle"
                    value={key}
                    checked={metaphorStyle === key}
                    onChange={(e) => setMetaphorStyle(e.target.value)}
                    className="mt-1 w-4 h-4 text-slate-900"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-slate-900">{style.name}</div>
                    <div className="text-sm text-slate-600 mb-2">{style.description}</div>
                    <div className="flex flex-wrap gap-2">
                      {style.examples.map((example, idx) => (
                        <span key={idx} className="px-2 py-1 bg-white rounded text-xs text-slate-700">
                          {example}
                        </span>
                      ))}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Personality Profile */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Personality Profile
            </h2>
            <p className="text-slate-600 mb-6">
              Provide personality context to help customize the language and approach in the insights.
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Motivation Style
                </label>
                <select
                  value={personalityProfile.motivationStyle}
                  onChange={(e) => setPersonalityProfile(prev => ({...prev, motivationStyle: e.target.value}))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                >
                  <option value="">Select motivation style...</option>
                  <option value="Achievement-oriented">Achievement-oriented</option>
                  <option value="Relationship-focused">Relationship-focused</option>
                  <option value="Autonomy-driven">Autonomy-driven</option>
                  <option value="Growth-minded">Growth-minded</option>
                  <option value="Impact-focused">Impact-focused</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Communication Preference
                </label>
                <select
                  value={personalityProfile.communicationPreference}
                  onChange={(e) => setPersonalityProfile(prev => ({...prev, communicationPreference: e.target.value}))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                >
                  <option value="">Select communication style...</option>
                  <option value="Direct and concise">Direct and concise</option>
                  <option value="Detailed and thorough">Detailed and thorough</option>
                  <option value="Encouraging and supportive">Encouraging and supportive</option>
                  <option value="Analytical and logical">Analytical and logical</option>
                  <option value="Creative and metaphorical">Creative and metaphorical</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Energy Source
                </label>
                <select
                  value={personalityProfile.energySource}
                  onChange={(e) => setPersonalityProfile(prev => ({...prev, energySource: e.target.value}))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                >
                  <option value="">Select energy source...</option>
                  <option value="Social interaction">Social interaction</option>
                  <option value="Independent work">Independent work</option>
                  <option value="Creative expression">Creative expression</option>
                  <option value="Problem solving">Problem solving</option>
                  <option value="Helping others">Helping others</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Decision Making
                </label>
                <select
                  value={personalityProfile.decisionMaking}
                  onChange={(e) => setPersonalityProfile(prev => ({...prev, decisionMaking: e.target.value}))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                >
                  <option value="">Select decision making style...</option>
                  <option value="Quick and intuitive">Quick and intuitive</option>
                  <option value="Careful and deliberate">Careful and deliberate</option>
                  <option value="Collaborative">Collaborative</option>
                  <option value="Data-driven">Data-driven</option>
                  <option value="Values-based">Values-based</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Feedback Style
                </label>
                <select
                  value={personalityProfile.feedbackStyle}
                  onChange={(e) => setPersonalityProfile(prev => ({...prev, feedbackStyle: e.target.value}))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                >
                  <option value="">Select feedback preference...</option>
                  <option value="Direct and honest">Direct and honest</option>
                  <option value="Gentle and encouraging">Gentle and encouraging</option>
                  <option value="Constructive with examples">Constructive with examples</option>
                  <option value="Action-oriented">Action-oriented</option>
                  <option value="Growth-focused">Growth-focused</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Identity Framework
                </label>
                <select
                  value={personalityProfile.identityFramework}
                  onChange={(e) => setPersonalityProfile(prev => ({...prev, identityFramework: e.target.value}))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                >
                  <option value="">Select identity framework...</option>
                  <option value="Individual achiever">Individual achiever</option>
                  <option value="Community contributor">Community contributor</option>
                  <option value="Lifelong learner">Lifelong learner</option>
                  <option value="Creative innovator">Creative innovator</option>
                  <option value="Service-oriented leader">Service-oriented leader</option>
                </select>
              </div>
            </div>
          </div>

          {/* Formatting Instructions */}
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Formatting Instructions
              </h2>
              
              <p className="text-slate-600 mb-4">
                Use Claude to generate specific formatting instructions based on your preferences.
              </p>

              {/* Copy Prompt */}
              <div className="bg-slate-50 rounded-lg p-4 mb-4">
                <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-slate-700">
                  Student-Specific Formatting Prompt (Copy this to Claude)
                </span>
                  <button
                    onClick={copyPromptToClipboard}
                    className={`flex items-center gap-2 px-3 py-1 rounded text-sm font-medium ${
                      copySuccess === 'copied' 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : copySuccess === 'failed'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                    }`}
                  >
                    {copySuccess === 'copied' ? (
                      <>
                        <CheckCircle className="w-3 h-3" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3" />
                        Copy Prompt
                      </>
                    )}
                  </button>
                </div>
                <div className="bg-white rounded border p-3 max-h-48 overflow-y-auto">
                  <pre className="text-xs text-slate-600 whitespace-pre-wrap">
                    {generateFormattingPrompt()}
                  </pre>
                </div>
              </div>

              {/* Paste Instructions */}
              <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
              Student-Specific Formatting Instructions (from Claude)
              </label>
              <textarea
                value={formattingInstructions}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setFormattingInstructions(newValue);
                  
                  // Validate the pasted instructions
                  if (newValue.length > 100) {
                    const has8Sections = newValue.includes('successMetrics');
                    const hasNestedStructure = newValue.includes('studentProfile') && newValue.includes('insights');
                    
                    if (!has8Sections) {
                      console.warn('⚠️ WARNING: Pasted instructions do not include successMetrics (8th section)');
                      console.warn('⚠️ This appears to be the OLD 7-section format');
                    }
                    
                    if (!hasNestedStructure) {
                      console.warn('⚠️ WARNING: Pasted instructions do not have nested studentProfile/insights structure');
                    }
                    
                    if (has8Sections && hasNestedStructure) {
                      console.log('✅ Pasted instructions appear to have correct 8-section nested format');
                    }
                  }
                }}
                placeholder={`Paste the complete JSON transformation instructions for ${studentProfile?.name || 'the student'} here...`}
                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 resize-none"
                rows="12"
              />

              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center">
            <button
              onClick={() => navigate('/college/synthesis-instructions')}
              className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Synthesis
            </button>

            <button
              onClick={saveFormattingPreferences}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save & Complete Framework
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <PreviewModal />
    </div>
  );
};

export default FormattingCustomizer;